/**
 * ViewModeToggle Component
 * 
 * Toggle between list, card, and table view modes.
 * Persists preference to localStorage per screen.
 */

import { useEffect } from 'react';
import { List, LayoutGrid, Table2, Columns } from 'lucide-react';
import type { ViewModeToggleProps, ViewMode, ViewModeConfig } from './types';

// ============================================================================
// View Mode Configurations
// ============================================================================

const VIEW_MODE_CONFIGS: Record<ViewMode, ViewModeConfig> = {
  list: {
    mode: 'list',
    icon: List,
    label: 'List view',
  },
  card: {
    mode: 'card',
    icon: LayoutGrid,
    label: 'Card view',
  },
  table: {
    mode: 'table',
    icon: Table2,
    label: 'Table view',
  },
  kanban: {
    mode: 'kanban',
    icon: Columns,
    label: 'Kanban view',
  },
};

// ============================================================================
// Local Storage Helpers
// ============================================================================

const STORAGE_KEY_PREFIX = 'collection_view_mode_';

export function getStoredViewMode(screenId: string): ViewMode | null {
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}${screenId}`);
    if (stored && (stored === 'list' || stored === 'card' || stored === 'table' || stored === 'kanban')) {
      return stored as ViewMode;
    }
  } catch (e) {
    // localStorage not available
  }
  return null;
}

export function storeViewMode(screenId: string, mode: ViewMode): void {
  try {
    localStorage.setItem(`${STORAGE_KEY_PREFIX}${screenId}`, mode);
  } catch (e) {
    // localStorage not available
  }
}

// ============================================================================
// Hook for persisted view mode
// ============================================================================

interface UsePersistedViewModeOptions {
  screenId: string;
  defaultMode?: ViewMode;
  onModeChange?: (mode: ViewMode) => void;
}

export function usePersistedViewMode({
  screenId,
  defaultMode = 'list',
  onModeChange,
}: UsePersistedViewModeOptions): [ViewMode, (mode: ViewMode) => void] {
  // Get initial mode from storage or default
  const storedMode = getStoredViewMode(screenId);
  const initialMode = storedMode || defaultMode;
  
  const handleModeChange = (mode: ViewMode) => {
    storeViewMode(screenId, mode);
    onModeChange?.(mode);
  };
  
  return [initialMode, handleModeChange];
}

// ============================================================================
// ViewModeToggle Component
// ============================================================================

export function ViewModeToggle({
  modes,
  currentMode,
  onModeChange,
  className = '',
}: ViewModeToggleProps) {
  // Only show if multiple modes are available
  if (modes.length <= 1) {
    return null;
  }
  
  return (
    <div className={`inline-flex items-center rounded-lg border border-gray-300 
                     bg-white overflow-hidden ${className}`}>
      {modes.map((mode) => {
        const config = VIEW_MODE_CONFIGS[mode];
        const Icon = config.icon;
        const isActive = mode === currentMode;
        
        return (
          <button
            key={mode}
            type="button"
            onClick={() => onModeChange(mode)}
            className={`p-2 transition-colors focus:outline-none focus:ring-2 
                        focus:ring-inset focus:ring-primary-500
                        ${isActive 
                          ? 'bg-primary-100 text-primary-700' 
                          : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
                        }`}
            title={config.label}
            aria-label={config.label}
            aria-pressed={isActive}
          >
            <Icon className="h-4 w-4" />
          </button>
        );
      })}
    </div>
  );
}

// ============================================================================
// ViewModeToggle with labels
// ============================================================================

interface ViewModeToggleWithLabelsProps extends ViewModeToggleProps {
  showLabels?: boolean;
}

export function ViewModeToggleWithLabels({
  modes,
  currentMode,
  onModeChange,
  showLabels = true,
  className = '',
}: ViewModeToggleWithLabelsProps) {
  if (modes.length <= 1) {
    return null;
  }
  
  return (
    <div className={`inline-flex items-center gap-1 ${className}`}>
      {modes.map((mode) => {
        const config = VIEW_MODE_CONFIGS[mode];
        const Icon = config.icon;
        const isActive = mode === currentMode;
        
        return (
          <button
            key={mode}
            type="button"
            onClick={() => onModeChange(mode)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium 
                        rounded-lg transition-colors focus:outline-none 
                        focus:ring-2 focus:ring-primary-500
                        ${isActive 
                          ? 'bg-primary-100 text-primary-700' 
                          : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'
                        }`}
            aria-pressed={isActive}
          >
            <Icon className="h-4 w-4" />
            {showLabels && <span className="capitalize">{mode}</span>}
          </button>
        );
      })}
    </div>
  );
}

export default ViewModeToggle;
